import React, { useState } from 'react';
import { Modal, Button, Form, Select, Card } from 'antd';
import { BankOutlined } from '@ant-design/icons';
import { Fee } from '../../types';
import { useCondoStore } from '../../store/condoStore';
import { useAuthStore } from '../../store/authStore';
import { staticApi } from '../../utils/staticApi';
// Sprint 146 (R14-21) — the modal read `Amount Due: $475` next to a table
// showing `$475.00`. Same number, two renderings, on a screen the operator is
// about to take money on.
import { currency } from '../../utils/currency';

const { Option } = Select;

interface PaymentFormProps {
  fee: Fee;
}

export const PaymentForm: React.FC<PaymentFormProps> = ({ fee }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [form] = Form.useForm();
  
  const { user } = useAuthStore();
  const { markFeeAsPaidApi } = useCondoStore();

  const handleSubmit = async () => {
    // Gate on a logged-in user only. The backend identity is the user UUID
    // (user.id), not the owners bigint (user.ownerId); the mark-paid call keys
    // on the fee id, so a present user is sufficient.
    if (!user?.id) return;

    setIsProcessing(true);

    try {
      // Real persistence: POST /api/fees/{id}/mark-paid then refetch.
      await markFeeAsPaidApi(fee.id);

      // Only close + reset AFTER the API resolves successfully.
      staticApi.message.success('Payment processed successfully');
      setIsOpen(false);
      form.resetFields();
    } catch (err: any) {
      // Keep the modal open so the user can retry.
      staticApi.message.error(err?.message ?? 'Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <Button 
        size="small" 
        type="primary"
        onClick={() => setIsOpen(true)}
      >
        Pay Now
      </Button>

      <Modal
        title={`Payment for ${fee.description}`}
        open={isOpen}
        onCancel={() => {
          setIsOpen(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Card size="small" className="mb-4">
          <div className="flex justify-between">
            <span>Amount Due:</span>
            <span className="font-bold">{currency(fee.amount)}</span>
          </div>
        </Card>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {/* R11-1 — this selector used to offer/default "Credit Card", but the
              selection was never sent: the mark-paid settlement path always
              records PaymentMethod.MANUAL, which displays as "Bank Transfer".
              Showing a card option that silently becomes a bank transfer is a
              trust bug on a money screen (same principle as 116.3 above). Offer
              only what the demo settlement actually records; real card capture
              routes through StripePaymentForm when payment_card_processing is on. */}
          <Form.Item
            name="paymentMethod"
            label="Payment Method"
            initialValue="bank-transfer"
          >
            <Select>
              <Option value="bank-transfer">
                <BankOutlined /> Bank Transfer (manual settlement)
              </Option>
            </Select>
          </Form.Item>

          {/* Sprint 116 (116.3) — the former card-number/expiry/CVV/cardholder
              inputs were REMOVED. They were never transmitted: this flow settles
              via markFeeAsPaidApi(fee.id) (the demo/mark-paid settlement path),
              which needs no card data. Collecting card fields that go nowhere is a
              trust hazard on a money screen. When real card capture is wired
              (payment_card_processing flag), route through StripePaymentForm
              instead of re-adding fake inputs here. */}

          <Card size="small" type="inner" className="mb-4">
            <p className="text-sm text-gray-600">
              Demo settlement: this marks the fee as paid for demonstration. No
              card details are collected and no real payment is processed.
            </p>
          </Card>

          <Form.Item className="mb-0">
            <div style={{display:"flex",justifyContent:"flex-end",gap:"12px"}}>
              <Button 
                onClick={() => {
                  setIsOpen(false);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={isProcessing}
              >
                Process Payment {currency(fee.amount)}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};